import { useEffect, useRef, useState } from 'react'
import { Globe, Check } from 'lucide-react'
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, type Language } from '../i18n'
import { useUiLanguage } from '../hooks/useUiLanguage'

interface LanguageSwitcherProps {
  /**
   * 'header'  — full pill with the current language name (dashboard header).
   * 'compact' — icon-only trigger (Login / Landing nav, tight spaces).
   */
  variant?: 'header' | 'compact'
  className?: string
}

export function LanguageSwitcher({ variant = 'header', className = '' }: LanguageSwitcherProps) {
  const { language, setLanguage } = useUiLanguage()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const choose = (lng: Language) => {
    setOpen(false)
    if (lng !== language) void setLanguage(lng)
  }

  return (
    <div className={`lang-switcher ${className}`} ref={ref}>
      <button
        type="button"
        className={`lang-switcher-btn lang-switcher-btn--${variant}`}
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={LANGUAGE_NAMES[language]}
      >
        <Globe size={variant === 'compact' ? 16 : 18} />
        {variant === 'header' && <span className="lang-switcher-current">{LANGUAGE_NAMES[language]}</span>}
      </button>

      {open && (
        <div className="lang-switcher-menu" role="listbox">
          {SUPPORTED_LANGUAGES.map(lng => (
            <button
              key={lng}
              type="button"
              role="option"
              aria-selected={lng === language}
              className={`lang-switcher-item${lng === language ? ' active' : ''}`}
              onClick={() => choose(lng)}
            >
              <span>{LANGUAGE_NAMES[lng]}</span>
              {lng === language && <Check size={15} />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LanguageSwitcher
